import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Spinner, Empty } from "../ui";
import { BarChart3, AlertTriangle, ChevronRight, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function fmt(n) {
  return (n == null ? 0 : n).toLocaleString("ro-RO");
}

function todayStr() {
  const d = new Date();
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function RapoarteCampanii() {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState(null);
  const [online, setOnline] = useState(true);
  const [updated, setUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [openRows, setOpenRows] = useState({});
  const [fromDate, setFromDate] = useState(todayStr());
  const [toDate, setToDate] = useState(todayStr());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/all-campaigns-report", {
        params: { from_date: fromDate, to_date: toDate },
      });
      setItems(data.items || []);
      setTotals(data.totals || null);
      setOnline(!!data.delivery_source_online);
      setUpdated(data.updated_at || "");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const setToday = () => { setFromDate(todayStr()); setToDate(todayStr()); };

  const toggle = (camp) => setOpenRows((s) => ({ ...s, [camp]: !s[camp] }));

  const cell = "px-3 py-2 border-b border-gray-200 text-black align-top";
  const num = cell + " text-right whitespace-nowrap";
  const head = "px-3 py-2 border-b-2 border-gray-400 font-bold text-black text-left whitespace-nowrap";
  const headNum = head + " text-right";

  return (
    <div style={{ fontFamily: "Arial", fontSize: "14px" }}>
      <PageHeader title="RAPOARTE CAMPANII" subtitle="Toate campaniile: total trimise, livrate, bounce, complaint per campanie si per provider. Doar super admin." />

      {loading ? <Spinner /> : (
        <>
          <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="rapoarte-daterange">
            <div className="flex flex-col">
              <label className="text-gray-500 mb-1">De la data</label>
              <input type="date" value={fromDate} max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)} data-testid="rapoarte-from-date"
                className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
                style={{ fontFamily: "Arial", fontSize: "14px" }} />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-500 mb-1">Pana la data</label>
              <input type="date" value={toDate} min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)} data-testid="rapoarte-to-date"
                className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
                style={{ fontFamily: "Arial", fontSize: "14px" }} />
            </div>
            <button onClick={setToday} data-testid="rapoarte-today-btn"
              className="px-3 py-2 rounded border border-gray-300 text-black hover:bg-gray-100">
              Azi
            </button>
          </div>

          {!online && (
            <div className="flex items-start gap-2 border border-gray-400 bg-gray-50 p-3 rounded mb-4" data-testid="rapoarte-offline-note">
              <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-gray-700">
                Datele de livrare nu au putut fi citite de pe serverul de campanii. Se afiseaza doar clicurile de activare din Yvola.
              </div>
            </div>
          )}

          {totals && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-9 gap-3 mb-4" data-testid="rapoarte-totals">
              {[
                ["Trimise", totals.trimise, "total-trimise"],
                ["Livrate", totals.livrate, "total-livrate"],
                ["Deschideri", totals.deschideri, "total-deschideri"],
                ["Clicuri", totals.clicuri, "total-clicuri"],
                ["Bounce", totals.bounce, "total-bounce"],
                ["Blocked", totals.blocked, "total-blocked"],
                ["Complaint", totals.complaint, "total-complaint"],
                ["Clic activare", totals.clic_activare, "total-clic"],
                ["Conturi activate", totals.conturi_activate, "total-conturi"],
              ].map(([label, val, tid]) => (
                <div key={tid} className="border border-gray-300 rounded p-3" data-testid={`rapoarte-${tid}`}>
                  <div className="text-gray-500">{label}</div>
                  <div className="text-black font-bold" style={{ fontSize: "16px" }}>{fmt(val)}</div>
                </div>
              ))}
            </div>
          )}

          {items.length === 0 ? (
            <Card><Empty icon={BarChart3} title="Nicio campanie" hint="Nu exista date de campanie inca." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap" data-testid="rapoarte-table" style={{ fontSize: "14px" }}>
                  <thead>
                    <tr>
                      <th className={head}>Campanie / Provider</th>
                      <th className={headNum}>Trimise</th>
                      <th className={headNum}>Livrate</th>
                      <th className={headNum}>Deschideri</th>
                      <th className={headNum}>Clicuri</th>
                      <th className={headNum}>Bounce</th>
                      <th className={headNum}>Blocked</th>
                      <th className={headNum}>Complaint</th>
                      <th className={headNum}>Clic activare</th>
                      <th className={headNum}>Conturi activate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c, i) => {
                      const provs = c.providers || [];
                      const isOpen = !!openRows[c.campaign];
                      return (
                        <React.Fragment key={c.campaign || i}>
                          <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => provs.length && toggle(c.campaign)}
                            data-testid={`rapoarte-campaign-row-${i}`}>
                            <td className={cell + " font-bold"}>
                              <span className="inline-flex items-center gap-1">
                                {provs.length ? (isOpen
                                  ? <ChevronDown className="w-4 h-4 text-gray-500" />
                                  : <ChevronRight className="w-4 h-4 text-gray-500" />) : <span className="w-4 h-4 inline-block" />}
                                {c.campaign || "-"}
                                {provs.length > 0 && <span className="text-gray-400 font-normal">({provs.length} provideri)</span>}
                              </span>
                            </td>
                            <td className={num + " font-bold"}>{fmt(c.trimise)}</td>
                            <td className={num + " font-bold"}>{fmt(c.livrate)}</td>
                            <td className={num + " font-bold"}>{fmt(c.deschideri)}</td>
                            <td className={num + " font-bold"}>{fmt(c.clicuri)}</td>
                            <td className={num + " font-bold"}>{fmt(c.bounce)}</td>
                            <td className={num + " font-bold"}>{fmt(c.blocked)}</td>
                            <td className={num + " font-bold"}>{fmt(c.complaint)}</td>
                            <td className={num + " font-bold"}>{fmt(c.clic_activare)}</td>
                            <td className={num + " font-bold"}>{fmt(c.conturi_activate)}</td>
                          </tr>
                          {isOpen && provs.map((p, j) => (
                            <tr key={`${i}-${j}`} className="bg-gray-50" data-testid={`rapoarte-provider-row-${i}-${j}`}>
                              <td className={cell + " pl-9 text-gray-700"}>{p.provider || "necunoscut"}</td>
                              <td className={num + " text-gray-700"}>{fmt(p.trimise)}</td>
                              <td className={num + " text-gray-700"}>{fmt(p.livrate)}</td>
                              <td className={num + " text-gray-400"}>-</td>
                              <td className={num + " text-gray-400"}>-</td>
                              <td className={num + " text-gray-700"}>{fmt(p.bounce)}</td>
                              <td className={num + " text-gray-700"}>{fmt(p.blocked)}</td>
                              <td className={num + " text-gray-700"}>{fmt(p.complaint)}</td>
                              <td className={num + " text-gray-400"}>-</td>
                              <td className={num + " text-gray-400"}>-</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          <div className="mt-3 text-gray-500" data-testid="rapoarte-updated">
            {updated ? `Actualizat: ${new Date(updated).toLocaleString("ro-RO")}` : ""}
          </div>
        </>
      )}
    </div>
  );
}
